import React, { useContext, useEffect, useRef, useState } from "react";
import { View, Text, StyleSheet, Pressable, Platform } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import routes from "../../../navigation/routes";
import UserContext from "../../../context/UserContext";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import { WebClass, FONT_BODY } from "../voxcraft/_shared";
import NtVoxcraftBrand from "../voxcraft/NtVoxcraftBrand";
import NtMarketingAccountPill from "./NtMarketingAccountPill";

// Sticky nav bar matching v4's `.nav`. On web we toggle `.scrolled` after the
// page has moved more than 8px so the bar gains an opaque bg + bottom border.
function NtMarketingPageContainerHeader() {
  const nav = useNavigation();
  const router = useRoute();
  const context = useContext(UserContext);
  const isMobile = useMobile();
  const { theme } = useTheme();
  const navRef = useRef(null);
  // Tracks which marketing section is currently in view so the matching nav
  // link can show its active underline. Only meaningful on the marketing
  // route; reset on every route change.
  const [activeSection, setActiveSection] = useState(null);

  // Scroll listener — adds `.scrolled` class on the nav node once the page
  // has scrolled past a small threshold. The actual scroller in this app is
  // the document body (it has overflow-y:auto in web/index.html), so we
  // listen on window. Visual change comes from the CSS in web/index.html.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const el = navRef.current && (navRef.current._node || navRef.current);
    if (!el) return;
    el.classList.add("voxcraft-nav");
    const onScroll = () => {
      if (window.scrollY > 8) el.classList.add("scrolled");
      else el.classList.remove("scrolled");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Anchors point to <View nativeID="..."> sections inside the marketing page.
  // On the marketing route we just scroll to the anchor; from any other route
  // we navigate to marketing first.
  const scrollToAnchor = (id) => {
    const doScroll = () => {
      if (typeof document === "undefined") return;
      const node = document.getElementById(id);
      if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (router?.name !== routes.marketing) {
      nav.navigate(routes.marketing);
      setTimeout(doScroll, 80);
    } else {
      doScroll();
    }
  };

  const links = [
    { id: "features",   title: "Features",   action: () => scrollToAnchor("features") },
    { id: "assistant",  title: "AI Setup",   action: () => scrollToAnchor("assistant") },
    { id: "industries", title: "Industries", action: () => scrollToAnchor("industries") },
    { id: "maritime",   title: "Maritime",   action: () => scrollToAnchor("maritime") },
    { id: "impact",     title: "Customers",  action: () => scrollToAnchor("impact") },
    { id: "security",   title: "Security",   action: () => scrollToAnchor("security") },
    { id: "pricing",    title: "Pricing",    action: () => scrollToAnchor("pricing") },
  ];

  // Scroll-spy — on every window scroll, recompute which section is in the
  // activation zone (top of viewport + a small offset) and mark its nav
  // link active. The body is the scroller in this layout (overflow-y:auto
  // in web/index.html), so we listen on window. Only runs on the marketing
  // route; cleared on every other route.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    if (router?.name !== routes.marketing) {
      setActiveSection(null);
      return;
    }
    const ids = links.map((l) => l.id);

    // Find the section whose top has scrolled just past the activation
    // line (sticky header bottom + breathing room). Walk in document order
    // and pick the last section whose top is at or above that line.
    const recompute = () => {
      const navEl = navRef.current && (navRef.current._node || navRef.current);
      const navH = navEl ? navEl.getBoundingClientRect().height : 60;
      const line = navH + 80;
      let next = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) next = id;
        else break;
      }
      setActiveSection(next);
    };

    recompute();
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        recompute();
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [router?.name]);

  return (
    <View ref={navRef} style={[styles.nav, Platform.OS === "web" && webSticky]}>
      <View style={[styles.inner, isMobile && styles.innerMobile]}>
        <NtVoxcraftBrand size={30} onPress={() => nav.navigate(routes.marketing)} />

        {!isMobile && (
          <View style={styles.links}>
            {links.map((l) => {
              const isActive = activeSection === l.id;
              return (
                <WebClass
                  key={l.id}
                  className={isActive ? "voxcraft-nav-link active" : "voxcraft-nav-link"}
                  style={styles.linkWrap}
                >
                  <Pressable onPress={l.action}>
                    <Text
                      dataSet={{ navLabel: true }}
                      style={[styles.linkText, { color: isActive ? theme.text : theme.textDim }]}
                    >
                      {l.title}
                    </Text>
                  </Pressable>
                </WebClass>
              );
            })}
          </View>
        )}

        <View style={styles.spacer} />

        <View style={styles.actions}>
          {context.user?.authenticated ? (
            // Signed in — show the account pill in place of Sign in / Get started.
            <NtMarketingAccountPill />
          ) : (
            <>
              <WebClass className="voxcraft-btn">
                <Pressable
                  onPress={() => nav.navigate(routes.auth)}
                  style={({ pressed }) => [styles.btn, styles.btnGhost, pressed && styles.btnPressed]}
                >
                  <Text style={[styles.btnText, { color: theme.text }]}>Sign in</Text>
                </Pressable>
              </WebClass>

              <WebClass className="voxcraft-btn voxcraft-btn-primary">
                <Pressable
                  onPress={() => nav.navigate(routes.onboarding)}
                  style={({ pressed }) => [styles.btn, styles.btnPrimary, { backgroundColor: theme.brand2 }, pressed && styles.btnPressed]}
                >
                  <Text style={[styles.btnText, { color: "#fff" }]}>Get started</Text>
                </Pressable>
              </WebClass>
            </>
          )}
        </View>
      </View>

      {isMobile && (
        <View style={styles.linksMobile}>
          {links.map((l) => (
            <Pressable key={l.id} onPress={l.action} style={styles.linkWrap}>
              <Text style={[styles.linkText, { color: theme.textDim }]}>{l.title}</Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
}

// Sticky positioning is web-only — RN doesn't support `position: sticky`,
// so we apply it via a Platform.OS check. RN-web honors raw web values
// passed through StyleSheet, which is why this works inside a style array.
const webSticky = { position: "sticky", top: 0, zIndex: 50 };

const styles = StyleSheet.create({
  // The voxcraft-nav CSS class also sets bg/blur/border; this provides the
  // baseline so the bar is visible even before the class mounts.
  nav: {
    width: "100%",
    backgroundColor: "rgba(4,5,6,0.7)",
    borderBottomWidth: 1,
    borderBottomColor: "transparent",
  },
  inner: {
    maxWidth: 1280, marginHorizontal: "auto", width: "100%",
    paddingHorizontal: 24, paddingVertical: 14,
    flexDirection: "row", alignItems: "center",
  },
  innerMobile: { paddingHorizontal: 16 },

  links: { flexDirection: "row", alignItems: "center", marginLeft: 24 },
  linkWrap: { marginRight: 22, paddingVertical: 4 },
  linkText: { fontFamily: FONT_BODY, fontSize: 13 },

  spacer: { flex: 1 },

  actions: { flexDirection: "row", alignItems: "center", gap: 10 },
  btn: {
    paddingHorizontal: 16, paddingVertical: 9,
    borderRadius: 8, borderWidth: 1, borderColor: "transparent",
    alignItems: "center", justifyContent: "center",
  },
  btnGhost: { backgroundColor: "transparent" },
  btnPrimary: {
    // brand2 set inline so it picks up theme; v4 also adds a colored shadow
    // — RN's shadow* props approximate this on web.
    shadowColor: "#2570BD", shadowOpacity: 0.35, shadowRadius: 16, shadowOffset: { width: 0, height: 6 },
  },
  btnText: { fontSize: 13, fontWeight: "600" },
  btnPressed: { opacity: 0.85 },

  // Mobile — links wrap onto a second row below the brand+actions row.
  linksMobile: {
    flexDirection: "row", flexWrap: "wrap",
    paddingHorizontal: 16, paddingBottom: 12,
    columnGap: 18, rowGap: 6,
  },
});

export default NtMarketingPageContainerHeader;
